import { defineCommand } from '../../../concepts/commands/CommandDefinition';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { inCommands } from '../../../concepts/commands/CommandRegistry';
import { ObjectManager } from '../../../concepts/objects';
import { SmartButton } from '../../packages/controls/SmartButton';
import { Camera } from '../Camera';
import { EditorSession } from '../EditorSession';
import { PointerInfo } from '../PointerInfo';
import { Phys } from '../entities/Phys';
import { LineLayout } from '../layout/LineLayout';
import { Pen } from '../layout/Pen';
import { PhysicalPlot } from '../layout/Plot';
import { Squash } from '../layout/Squash';
import { EffectDirector } from '../visualization/EffectDirector';
import { resolveSubjects } from './CommandUtil';
import { bringToFront, place, unplace } from './OrderCommands';

export const organizeHorizontally = defineCommand(
    'organizeHorizontally',
    'Organize: Horizontally',
    (r, [leaveAtRoot, targetIds]: [boolean?, string[]?]) => {

        const camera = r.call(Camera);
        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const subjects = resolveSubjects(r, targetIds);

        if (!subjects.length) return;

        objects.update(() => {

            dispatch(unplace, [targetIds]);
            dispatch(bringToFront, [targetIds]);

            const vpt = session.get(PointerInfo).position;
            const mpt = camera.toModel(vpt);

            const pen = Pen.from(subjects);
            LineLayout.computeHorizontal(pen, 10)
                .moveTo(mpt.x, mpt.y)
                .alignCenter()
                .moveBy(0, pen.height() / -2);

            pen.apply();

            if (!leaveAtRoot) {
                dispatch(place, [targetIds]);
            }

        });
    }
);

export const organizeVertically = defineCommand(
    'organizeVertically',
    'Organize: Vertically',
    (r, [leaveAtRoot, targetIds]: [boolean?, string[]?]) => {

        const camera = r.call(Camera);
        const dispatch = r.call(CommandDispatcher);
        const objects = r.call(ObjectManager);
        const session = r.call(EditorSession);
        const subjects = resolveSubjects(r, targetIds);

        if (!subjects.length) return;

        objects.update(() => {

            dispatch(unplace, [targetIds]);
            dispatch(bringToFront, [targetIds]);

            const vpt = session.get(PointerInfo).position;
            const mpt = camera.toModel(vpt);

            const pen = Pen.from(subjects);
            LineLayout.computeVertical(pen, 10)
                .moveTo(mpt.x, mpt.y)
                .justifyCenter()
                .moveBy(pen.width() / -2, 0);

            pen.apply();

            if (!leaveAtRoot) {
                dispatch(place, [targetIds]);
            }

        });
    }
);

export const organizeSquash = defineCommand(
    'organizeSquash',
    'Organize: Squash',
    (r, [targetIds]: [string[]?]) => {

        const camera = r.resolve(Camera);
        const dispatch = r.resolve(CommandDispatcher);
        const objects = r.resolve(ObjectManager);
        const session = r.resolve(EditorSession);
        const effects = r.resolve(EffectDirector);
        const subjects = resolveSubjects(r, targetIds).filter(x => !x.is(SmartButton));

        // Find the smallest item
        if (subjects.length <= 1) return;

        const initial = Phys.from(subjects).smallest();
        const rest = subjects.filter(x => x !== initial);

        const scwosh = new Squash(PhysicalPlot.for(initial));

        for (const s of rest) {
            scwosh.add(PhysicalPlot.for(s));
        }

        objects.update(() => {
            scwosh.apply();
        });

    }
);

export const inOrganizeCommands = () => inCommands(organizeHorizontally, organizeVertically, organizeSquash);
